import React, {useEffect, useMemo, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {MultiSelect} from 'primereact/multiselect';
import {Message} from 'primereact/message';
import {ProgressBar} from 'primereact/progressbar';
import axios from 'axios';

const EmployeeUserAccountComponent = ({employeeId, t}) => {
  const roleCodes = (localStorage.getItem('roles') || '').toLowerCase();
  const canManageAccounts = ['role_user_write', 'role_role_group_read', 'role_set_role']
    .every(role => roleCodes.includes(role));
  const [employee, setEmployee] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [roleGroups, setRoleGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedRoleGroups, setSelectedRoleGroups] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [inviteVisible, setInviteVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => ({
    Authorization: 'Bearer ' + localStorage.getItem('userToken'),
    'App-Network': localStorage.getItem('networkId')
  }), []);

  const roleNames = useMemo(() => roles.reduce((result, role) => {
    result[role.code] = role.name;
    return result;
  }, {}), [roles]);

  const getRoleName = roleCode => roleNames[roleCode] || roleCode;

  const loadAccounts = () => {
    setLoading(true);
    axios.get(process.env.REACT_APP_API_HOST + `/api/v2/employee/${employeeId}`, {headers})
      .then(employeeResponse => {
      const employeeData = employeeResponse.data.data;
      setEmployee(employeeData);
      setAccounts(employeeData?.users || []);
    }).catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAccounts();
  }, [employeeId]);

  useEffect(() => {
    axios.get(process.env.REACT_APP_API_HOST + '/api/v2/dictionary/role?limit=9999', {headers})
      .then(response => setRoles(response.data?.data || []))
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response));
  }, [headers, t]);

  useEffect(() => {
    if (!canManageAccounts) return;
    axios.get(process.env.REACT_APP_API_HOST + '/api/v2/role-group?item_per_page=9999', {headers})
      .then(response => setRoleGroups(response.data?.data?.items || []))
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response));
  }, [canManageAccounts, headers]);

  const inviteUser = () => {
    setLoading(true);
    axios.post(process.env.REACT_APP_API_HOST + `/api/v2/employee/${employeeId}/user/invite`, {
      role_groups: selectedRoleGroups,
      direct_roles: selectedRoles
    }, {headers}).then(() => {
      window.App.toastShow('success', t('success'), t('userInvitationSent'));
      setInviteVisible(false);
      setSelectedRoleGroups([]);
      setSelectedRoles([]);
      loadAccounts();
    }).catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  };

  return (
    <div className="p-position-relative">
      {loading && <ProgressBar mode="indeterminate" style={{height: 4}}/>}
      {employee !== null && (accounts.length > 0 ? accounts.map(user => (
        <div key={user.id} className="p-p-3 p-mb-3" style={{border: '1px solid #dee2e6', borderRadius: 6}}>
          <div className="p-mb-3">
            <div>
              <h3 className="p-m-0">{user.name}</h3>
              <div className="p-text-secondary p-mt-1">{user.email || user.identifier}</div>
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col-12 p-lg-6">
              <strong>{t('roleGroups')}</strong>
              <div className="p-mt-2">{(user.role_groups || []).length
                ? user.role_groups.map(group => <span key={group.id} className="p-tag p-mr-1 p-mb-1">{group.name}</span>)
                : <span className="p-text-secondary">{t('none')}</span>}</div>
            </div>
            <div className="p-col-12 p-lg-6">
              <strong>{t('individualPermissions')}</strong>
              <div className="p-mt-2">{(user.direct_roles || []).length
                ? user.direct_roles.map(role => <span key={role} className="p-tag p-tag-info p-mr-1 p-mb-1">{getRoleName(role)}</span>)
                : <span className="p-text-secondary">{t('none')}</span>}</div>
            </div>
            <div className="p-col-12">
              <strong>{t('effectivePermissions')}</strong>
              <div className="p-mt-2">{(user.roles || []).length
                ? user.roles.map(role => <span key={role} className="p-tag p-tag-success p-mr-1 p-mb-1">{getRoleName(role)}</span>)
                : <span className="p-text-secondary">{t('none')}</span>}</div>
            </div>
          </div>
        </div>
      )) : (
        <div>
          <Message severity="info" text={t('employeeHasNoUserAccount')} />
          {canManageAccounts && <div className="p-mt-3">
            <Button label={t('inviteNewUser')} icon="pi pi-envelope" className="p-button-success"
                    disabled={!employee?.email}
                    onClick={() => setInviteVisible(true)}/>
          </div>}
          {canManageAccounts && !employee?.email && <div className="p-error p-mt-2">{t('inviteUnavailableWithoutEmail')}</div>}
        </div>
      ))}

      <Dialog visible={inviteVisible} header={t('inviteNewUser')} modal style={{width: '50rem', maxWidth: '95vw'}}
              onHide={() => setInviteVisible(false)} footer={<>
                <Button label={t('cancel')} className="p-button-text" onClick={() => setInviteVisible(false)}/>
                <Button label={t('sendInvitation')} icon="pi pi-envelope" className="p-button-success" onClick={inviteUser}/>
              </>}>
        <Message severity="info" text={t('invitationValidFor24Hours')} />
        <div className="p-grid p-mt-2">
          <div className="p-col-12 p-lg-6 p-field p-fluid">
            <label>{t('roleGroups')}</label>
            <MultiSelect value={selectedRoleGroups} options={roleGroups} optionValue="id" optionLabel="name" filter
                         onChange={e => setSelectedRoleGroups(e.value)} placeholder={t('selectRoleGroups')}/>
          </div>
          <div className="p-col-12 p-lg-6 p-field p-fluid">
            <label>{t('individualPermissions')}</label>
            <MultiSelect value={selectedRoles} options={roles} optionValue="code" optionLabel="name" filter
                         onChange={e => setSelectedRoles(e.value)} placeholder={t('selectPermissions')}/>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default withTranslation('common')(EmployeeUserAccountComponent);
